/* External */
import { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/* Components */
import FocusableButton from '../FocusableButton';

/* Utils */
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import { useOnBackPress } from '../../utils/keyDownEvents';
import { useGtmLocalContext } from '../../providers/GtmLocalProvider';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../utils/googleTags';

function ContentModal({
  logo,
  image,
  icon,
  steps,
  currentStep,
  title,
  description,
  textButton,
  isModalShown,
  onEnterPress,
  onArrowPress,
  handleOnBack,
  realFocusKey,
  setFocus,
}) {
  const buttonFocusKey = `${realFocusKey}_button`;
  const tagsStepOneMountRef = useRef(true);
  const tagsStepTwoMountRef = useRef(true);
  const { commonTaggingEvent } = useGtmLocalContext();

  useEffect(() => {
    if (isModalShown) {
      setFocus(buttonFocusKey);
    }
  }, [setFocus, buttonFocusKey, isModalShown]);

  useOnBackPress({
    listener: handleOnBack,
    when: isModalShown,
  });

  useEffect(() => {
    if (tagsStepOneMountRef.current && currentStep === 'stepOne') {
      commonTaggingEvent({
        category: GTM_CATEGORIES.MODAL_DATA,
        subCategory: GTM_SUBCATEGORIES.MODAL.INTERACTIVITY.STEP_ONE,
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
      });
      tagsStepOneMountRef.current = false;
    }

    if (tagsStepTwoMountRef.current && currentStep === 'stepTwo') {
      commonTaggingEvent({
        category: GTM_CATEGORIES.MODAL_DATA,
        subCategory: GTM_SUBCATEGORIES.MODAL.INTERACTIVITY.STEP_TWO,
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
      });
      tagsStepTwoMountRef.current = false;
    }
  }, [currentStep, commonTaggingEvent]);

  return (
    <div className="dtv-html5-c-tappp-modal__content">
      <div className="dtv-html5-c-tappp-modal__header">
        <img loading="lazy" decoding="async" src={logo} className="dtv-html5-c-tappp-modal__logo" alt="logo" />
      </div>
      <div className="dtv-html5-c-tappp-modal__body">
        <div className="dtv-html5-c-tappp-modal__message-wrapper">
          <h1 className="dtv-html5-c-tappp-modal__title">
            {title}
          </h1>
          <div className="dtv-html5-c-tappp-modal__description-wrapper">
            {icon && <img loading="lazy" decoding="async" src={icon} className="dtv-html5-c-tappp-modal__icon" alt="icon" />}
            <p className="dtv-html5-c-tappp-modal__description">
              {description}
            </p>
          </div>
        </div>
        <div className="dtv-html5-c-tappp-modal__image-wrapper">
          <img loading="lazy" decoding="async" src={image} className="dtv-html5-c-tappp-modal__image" alt="interactivity-step" />
        </div>
      </div>
      <div className="dtv-html5-c-tappp-modal__footer">
        <div className="dtv-html5-c-tappp-modal__position-indicators">
          {steps.map((step) => (
            <span
              className={`
                dtv-html5-c-tappp-modal__indicator
                ${step === currentStep
                ? 'dtv-html5-c-tappp-modal__indicator--active'
                : ''
                }
            `}
            />
          ))}

        </div>
        <FocusableButton
          className="dtv-html5-c-tappp-modal__button"
          onArrowPress={onArrowPress}
          onEnterPress={onEnterPress}
          focusKey={buttonFocusKey}
          primary
          bordered
        >
          {textButton}
        </FocusableButton>
      </div>
    </div>
  );
}

ContentModal.propTypes = {
  logo: PropTypes.node.isRequired,
  image: PropTypes.node.isRequired,
  icon: PropTypes.node,
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentStep: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  description: PropTypes.node.isRequired,
  textButton: PropTypes.node.isRequired,
  isModalShown: PropTypes.bool.isRequired,
  onEnterPress: PropTypes.func.isRequired,
  onArrowPress: PropTypes.func.isRequired,
  handleOnBack: PropTypes.func.isRequired,
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withInteractiveFocusable()(memo(ContentModal)));
